import { Trans } from '@lingui/macro'
import { useWeb3React } from '@web3-react/core'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components/macro'

import { AutoColumn } from '../../components/Column'
import DoubleCurrencyLogo from '../../components/DoubleLogo'
import IncentivePositions from '../../components/IncentivePositions'
import { PageWrapper } from '../../components/swap/styleds'
import { useCurrency } from '../../hooks/Tokens'
import { useIncentiveConfig } from '../../hooks/useIncentive'
import { ThemedText } from '../../theme/components/text'
import { getIncentiveStatus } from '../../utils/incentives'
import { IncentiveActions } from './IncentiveActions'

const HoverText = styled(ThemedText.DeprecatedMain)`
  text-decoration: none;
  color: ${({ theme }) => theme.textSecondary};
  :hover {
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: none;
  }
`

const IncentivePageWrapper = styled(PageWrapper)`
  display: flex;
  flex-direction: column;
  padding: 68px 8px 0px;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 870px;

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    max-width: 800px;
  `};

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    max-width: 500px;
  `};

  @media screen and (max-width: ${({ theme }) => theme.breakpoint.md}px) {
    padding-top: 48px;
  }

  @media screen and (max-width: ${({ theme }) => theme.breakpoint.sm}px) {
    padding-top: 20px;
  }
`

const IncentiveHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  width: 100%;

  @media screen and (max-width: 721px) {
    flex-direction: column;
    align-items: center;
  }
`

const LogoContainer = styled.div`
  @media screen and (max-width: 721px) {
    display: flex;
    justify-content: center;
    width: 100%;
    margin-bottom: 16px;
  }
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media screen and (max-width: 721px) {
    align-items: center;
    text-align: center;
  }
`

const TitleContentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  margin-top: 4px;

  @media screen and (max-width: 721px) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`

const StatusContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 721px) {
    justify-content: center;
  }
`

const DateText = styled(ThemedText.BodySmall)`
  color: ${({ theme }) => theme.textSecondary};
`

const StatusBadge = styled.div<{ status: 'active' | 'upcoming' | 'expired' }>`
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
  background-color: ${({ theme, status }) =>
    status === 'active' ? theme.accentSuccess : status === 'upcoming' ? theme.accentAction : theme.backgroundOutline};
  color: ${({ theme, status }) => (status === 'active' || status === 'upcoming' ? theme.white : theme.textSecondary)};
`

// Helper function to format date with time
function formatDateWithTime(timestamp: number): string {
  const date = new Date(timestamp * 1000)

  // Format date as DD/MM/YYYY
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()

  // Format time as HH:MM
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')

  return `${day}/${month}/${year}, ${hours}:${minutes}`
}

export default function IncentivePage() {
  const { incentiveId } = useParams<{ incentiveId: string }>()
  const { account } = useWeb3React()

  const incentive = useIncentiveConfig(incentiveId)

  // Call useCurrency hooks outside the conditional
  const tokenA = useCurrency(incentive?.tokenIdA)
  const tokenB = useCurrency(incentive?.tokenIdB)

  // If no incentive is found, redirect to the stake page
  if (!incentive) {
    return null
  }

  const status = getIncentiveStatus(incentive)

  // Check if the current user is the refundee
  const isRefundee = account && incentive.key.refundee && account.toLowerCase() === incentive.key.refundee.toLowerCase()

  // Format dates for display with time
  const startDate = formatDateWithTime(incentive.key.startTime)
  const endDate = formatDateWithTime(incentive.key.endTime)

  // Add fee tier to the incentive name
  const feeTierPercentage = (incentive.fee / 10000).toFixed(2)
  const incentiveNameWithFeeTier = `${incentive.name} (${feeTierPercentage}%)`

  return (
    <IncentivePageWrapper>
      <AutoColumn gap="lg" justify="center" style={{ width: '100%' }}>
        <AutoColumn gap="lg" style={{ width: '100%' }}>
          <Link style={{ textDecoration: 'none', width: 'fit-content', marginBottom: '0.5rem' }} to="/stake">
            <HoverText>
              <Trans>← Back</Trans>
            </HoverText>
          </Link>
          <IncentiveHeader>
            <LogoContainer>
              <DoubleCurrencyLogo currency0={tokenA ?? undefined} currency1={tokenB ?? undefined} size={48} margin />
            </LogoContainer>
            <TitleContainer>
              <ThemedText.LargeHeader>{incentiveNameWithFeeTier}</ThemedText.LargeHeader>
              <TitleContentContainer>
                <StatusContainer>
                  <StatusBadge status={status}>
                    <Trans>{status.charAt(0).toUpperCase() + status.slice(1)}</Trans>
                  </StatusBadge>
                  <DateText>
                    {startDate} - {endDate}
                  </DateText>
                </StatusContainer>
                {status === 'expired' && isRefundee && <IncentiveActions incentive={incentive} />}
              </TitleContentContainer>
            </TitleContainer>
          </IncentiveHeader>

          <IncentivePositions incentiveId={incentiveId} />
        </AutoColumn>
      </AutoColumn>
    </IncentivePageWrapper>
  )
}
